import React, { useRef, useState, useEffect, useContext } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { ContextBoard } from "../../context/ContextBoard";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Footer from "../shared/Footer";
import Navbar from "../shared/Navbar";
import Sidebar from "../shared/Sidebar";
import config from "../../config";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { MdOutlineKeyboardDoubleArrowRight } from "react-icons/md";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { MdOutlineKeyboardDoubleArrowLeft } from "react-icons/md";
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaEye,
  FaEyeSlash,
  FaCopy,
  FaCheck,
  FaTimes,
  FaEdit,
  FaKey,
  FaLock,
  FaLockOpen,
} from "react-icons/fa";

function Schoollist() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("secondaryAdminToken");
  const { setSecondaryAdminSchoolCode } = useContext(ContextBoard);
  const tableRef = useRef(null);
  const [openDropdown, setOpenDropdown] = useState(false);
  const toggleDropdown = (code) => {
    setOpenDropdown(openDropdown === code ? false : code);
  };
  const [loading, setLoading] = useState(true);
  const [districtList, setDistrictList] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [schoolList, setSchoolList] = useState([]);
  const [schoolCode, setSchoolCode] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [copied, setCopied] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [currentPasswordVisible, setCurrentPasswordVisible] = useState("");
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  // Strength Labels & Colors
  const strengthLabels = ["Very Weak", "Weak", "Fair", "Good", "Strong"];
  const strengthColors = [
    "#e53935",
    "#fb8c00",
    "#fdd835",
    "#43a047",
    "#1e88e5",
  ];

  // Strength Score
  const passwordStrength = {
    score:
      newPassword.length > 10
        ? 4
        : newPassword.length > 8
        ? 3
        : newPassword.length > 5
        ? 2
        : newPassword.length > 2
        ? 1
        : 0,
  };

  // Validation Rules
  const rules = [
    { valid: newPassword.length >= 8, label: "Minimum 8 characters" },
    { valid: /[A-Z]/.test(newPassword), label: "One uppercase letter" },
    { valid: /[a-z]/.test(newPassword), label: "One lowercase letter" },
    { valid: /\d/.test(newPassword), label: "One number" },
    {
      valid: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      label: "One special character",
    },
  ];

  // Tooltip
  const renderTooltip = (props) => (
    <Tooltip
      id="password-tooltip"
      {...props}
      style={{ ...props.style, zIndex: 1200 }}
    >
      <div className="small">
        {/* Strength Bar */}
        <div
          className="progress mb-2"
          style={{ height: "6px", width: "180px" }}
        >
          <div
            className="progress-bar"
            style={{
              width: `${(passwordStrength.score + 1) * 20}%`,
              backgroundColor: strengthColors[passwordStrength.score],
            }}
          ></div>
        </div>

        {/* Strength Label */}
        <div
          className="mb-2"
          style={{
            color: strengthColors[passwordStrength.score],
            fontWeight: "500",
          }}
        >
          {strengthLabels[passwordStrength.score]}
        </div>

        {/* Rules List */}
        {rules.map((rule, idx) => (
          <div key={idx} style={{ color: rule.valid ? "green" : "red" }}>
            {rule.valid ? <FaCheck /> : <FaTimes />} {rule.label}
          </div>
        ))}
      </div>
    </Tooltip>
  );

  const fetchDistrict = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config.API_BASE_URL}/AdminPageLoadDistrictDataSecondary`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (response.ok && data.data.district_list) {
        setDistrictList(data.data.district_list);
      } else {
        Swal.fire("Error", "Failed to load districts", "error");
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/secondary-admin");
    } else {
      fetchDistrict();
    }
  }, []);

  const handleSearch = async () => {
    if (!selectedDistrict) {
      Swal.fire("Error", "Please select a district", "error");
      return;
    } else {
      setLoading(true);
      try {
        const response = await fetch(
          `${config.API_BASE_URL}/AdminFetchSchoolDataByDistSecondary`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              districtcode: selectedDistrict,
            }),
          }
        );

        const data = await response.json();
        if (response.ok && data.data.distWiseSchool_list) {
          setSchoolList(data.data.distWiseSchool_list);
        } else {
          Swal.fire("Error", "Failed to load schools", "error");
        }
      } catch (error) {
        Swal.fire("Error", error.message || "Something went wrong", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const filteredData = schoolList.filter((school) => {
    const query = searchQuery.toLowerCase();
    return (
      school.schooL_NAME?.toLowerCase().includes(query) ||
      school.schooL_CODE?.toLowerCase().includes(query) ||
      school.blocK_NAME?.toLowerCase().includes(query) ||
      school.sC_ADDRESS?.toLowerCase().includes(query) ||
      school.schooL_PASSWORD?.toLowerCase().includes(query) ||
      school.sC_STATUS?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(filteredData.length / perPage);
  const indexOfLastItem = currentPage * perPage;
  const indexOfFirstItem = indexOfLastItem - perPage;

  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };
  const handleCopy = (password, id) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(password)
        .then(() => {
          setCopied(id);
          setTimeout(() => setCopied(null), 1000);
        })
        .catch((err) => console.error("Clipboard error:", err));
    } else {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = password;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(id);
      setTimeout(() => setCopied(null), 1000);
    }
  };
  const openPasswordModal = (schooL_CODE, schooL_PASSWORD) => {
    setNewPassword("");
    setConfirmPassword("");
    setShowEditPassword(true);
    setSchoolCode(schooL_CODE);
    setCurrentPasswordVisible(schooL_PASSWORD);
  };
  const closePasswordModal = () => {
    setSchoolCode("");
    setNewPassword("");
    setConfirmPassword("");
    setShowEditPassword(false);
    setCurrentPasswordVisible("");
  };
  const handleSubmit = async () => {
    if (!newPassword) {
      return toast.error("password fields must be filled out.");
    }

    /* if (newPassword !== confirmPassword) {
      return toast.error(
        "Passwords do not match. Please enter the same password in both fields."
      );
    } 
    const failedRules = rules.filter((rule) => !rule.valid);

    if (failedRules.length > 0) {
      toast.error(
        <div>
          <strong>Password must have:</strong>
          <ul>
            {failedRules.map((r, i) => (
              <li key={i}>{r.label}</li>
            ))}
          </ul>
        </div>
      );
      return;
    } */
    setLoading(true);
    try {
      if (!schoolCode) {
        return toast.error("Please select a school before updating password.");
      }

      const response = await fetch(
        `${config.API_BASE_URL}/AdminUpdatePasswordSchoolSecondary`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            schooL_CODE: schoolCode,
            neW_PASSWORD: newPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Password updated successfully!",
          showConfirmButton: true,
          timer: 3000,
        });
        closePasswordModal();
        handleSearch();
      } else {
        throw new Error(data.message || "An error occurred");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };
  const handleChangeSchoolStatus = async (schoolCode, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Do you really want to change status to ${newStatus.toLowerCase()}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (confirm.isConfirmed) {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}/AdminUpdateSCStatusSchoolSecondary`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              schooL_CODE: schoolCode,
              sC_STATUS: newStatus,
            }),
          }
        );

        const data = await response.json();

        if (response.ok) {
          Swal.fire({
            icon: "success",
            title: "School status updated",
            text: `School status changed to ${newStatus.toLowerCase()} successfully!`,
            showConfirmButton: false,
            timer: 2000,
          });
          handleSearch();
          setOpenDropdown(false);
        } else {
          throw new Error(data.message || "An error occurred");
        }
      } catch (error) {
        Swal.fire("Error", error.message || "Something went wrong", "error");
      }
    }
  };
  const generatePassword = () => {
    const length = 10;
    const uppercase = "ABCDEFGHKMNPQRSTUVWXYZ";
    const numeric = "2345689";
    const allchars = uppercase + numeric;

    let pwd = "";
    // Ensure at least one uppercase
    pwd += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    // Ensure at least one number
    pwd += numeric.charAt(Math.floor(Math.random() * numeric.length));

    // Fill remaining with random allowed characters
    for (let i = pwd.length; i < length; i++) {
      pwd += allchars.charAt(Math.floor(Math.random() * allchars.length));
    }

    // Shuffle password
    pwd = pwd
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");

    setNewPassword(pwd);
  };

  return (
    <>
      <div className="container-scroller">
        <ToastContainer position="top-right" autoClose={3000} />
        {loading && (
          <div className="custom-loader-container">
            <div className="custom-loader"></div>
          </div>
        )}
        <Navbar />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper">
              <div className="row">
                <div className="col-md-12 mb-3">
                  <section className="content-header">
                    <h1>
                      <i className="fa fa-university" />
                      &nbsp;School's List
                    </h1>
                    <ol className="breadcrumb">
                      <li>
                        <a
                          onClick={() => navigate("/secondary-admin-dashboard")}
                          style={{ cursor: "pointer" }}
                        >
                          <i
                            className="fa fa-tachometer"
                            style={{ marginRight: "5px" }}
                          ></i>{" "}
                          Home
                        </a>
                      </li>
                      <li className="active">Secondary Admin School List</li>
                    </ol>
                  </section>
                </div>
              </div>
              <div className="row">
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Select District</label>
                          {/* <select
                              className="form-select form-select-xs"
                              value={selectedDistrict}
                              onChange={(e) => setSelectedDistrict(e.target.value)}
                          >
                              <option value="">Select</option>
                              {districtList.map((district) => (
                                  <option key={district.districT_CODE} value={district.districT_CODE}>
                                      {district.district} - {district.districT_CODE}
                                  </option>
                              ))}
                          </select> */}
                          <Select
                            options={districtList.map((district) => ({
                              label: `${district.district} - ${district.districT_CODE}`,
                              value: district.districT_CODE,
                            }))}
                            onChange={(selectedOption) =>
                              setSelectedDistrict(selectedOption?.value || "")
                            }
                            isClearable
                            placeholder="Select district"
                          />
                        </div>
                      </div>
                      <div className="col-md-1">
                        <div className="form-group">
                          <label>&nbsp;</label>
                          <br />
                          <button
                            className="btn btn-info"
                            onClick={handleSearch}
                          >
                            <i className="fa fa-search"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    {schoolList.length > 0 && (
                      <div className="row">
                        <hr />
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center">
                            <label htmlFor="rowsPerPage" className="me-2 mb-0">
                              Rows:
                            </label>
                            <div style={{ width: "250px" }}>
                              <select
                                id="rowsPerPage"
                                className="form-select form-select-sm"
                                value={perPage}
                                onChange={handlePerPageChange}
                                style={{
                                  width: "80px", // or adjust as needed (80–120px)
                                  paddingRight: "2rem", // ensures space for the dropdown arrow
                                }}
                              >
                                {[10, 20, 50, 100, 500].map((num) => (
                                  <option key={num} value={num}>
                                    {num}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div style={{ width: "200px" }}>
                            <input
                              type="text"
                              autoComplete="new-search"
                              className="form-control form-control-sm"
                              placeholder="Search..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                          </div>
                        </div>

                        <div
                          className="table-responsive"
                          style={{ minHeight: "165px" }}
                        >
                          <table
                            id="studentTable"
                            className="table table-bordered table-striped table-hover"
                            ref={tableRef}
                          >
                            <thead>
                              <tr>
                                <th>Sl No.</th>
                                <th>Block</th>
                                <th>School Name</th>
                                <th>School Code</th>
                                <th>Address</th>
                                <th>Password</th>
                                <th>Status</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentItems.map((row, index) => (
                                <tr key={row.schooL_CODE}>
                                  <td>{index + 1}</td>
                                  <td>{row.blocK_NAME}</td>
                                  <td>{row.schooL_NAME}</td>
                                  <td>{row.schooL_CODE}</td>
                                  <td>{row.sC_ADDRESS}</td>
                                  <td style={{ width: "100px" }}>
                                    <div
                                      className="d-flex align-items-center gap-2 px-2 py-1 "
                                      style={{
                                        cursor: "pointer",
                                        width: "150px",
                                      }}
                                      onClick={() =>
                                        handleCopy(
                                          row.schooL_PASSWORD || "",
                                          row.schooL_CODE
                                        )
                                      }
                                      title="Click to copy password"
                                    >
                                      <span className="text-monospace text-dark">
                                        {row.schooL_PASSWORD || ""}
                                      </span>

                                      {copied === row.schooL_CODE ? (
                                        <FaCheck
                                          size={14}
                                          className="text-success ms-1"
                                        />
                                      ) : (
                                        <FaCopy
                                          size={14}
                                          className="text-secondary ms-1"
                                        />
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    {row.sC_STATUS === "ACTIVE" ? (
                                      <small className="badge bg-success">
                                        Active
                                      </small>
                                    ) : (
                                      <small className="badge bg-danger">
                                        Inactive
                                      </small>
                                    )}
                                  </td>

                                  <td>
                                    <div
                                      className={`dropdown ${
                                        openDropdown === row.schooL_CODE
                                          ? "show"
                                          : ""
                                      }`}
                                    >
                                      <button
                                        type="button"
                                        className="btn btn-primary dropdown-toggle"
                                        onClick={() =>
                                          toggleDropdown(row.schooL_CODE)
                                        }
                                      >
                                        Action
                                      </button>

                                      {openDropdown === row.schooL_CODE && (
                                        <div
                                          className="dropdown-menu dropdown-menu-end show"
                                          style={{ right: 0, left: "auto" }}
                                        >
                                          <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              setSecondaryAdminSchoolCode(
                                                row.schooL_CODE
                                              );
                                              navigate(
                                                "/secondary-admin-edit-school"
                                              );
                                              setOpenDropdown(false);
                                            }}
                                          >
                                            <FaEdit /> Edit
                                          </button>

                                          <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              openPasswordModal(
                                                row.schooL_CODE,
                                                row.schooL_PASSWORD
                                              );
                                              setOpenDropdown(false);
                                            }}
                                          >
                                            <FaKey /> Change Password
                                          </button>

                                          {row.sC_STATUS === "ACTIVE" ? (
                                            <button
                                              type="button"
                                              className="dropdown-item text-danger"
                                              onClick={() =>
                                                handleChangeSchoolStatus(
                                                  row.schooL_CODE,
                                                  row.sC_STATUS
                                                )
                                              }
                                            >
                                              <FaLock /> Inactive
                                            </button>
                                          ) : (
                                            <button
                                              type="button"
                                              className="dropdown-item text-success"
                                              onClick={() =>
                                                handleChangeSchoolStatus(
                                                  row.schooL_CODE,
                                                  row.sC_STATUS
                                                )
                                              }
                                            >
                                              <FaLockOpen /> Active
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              {currentItems.length === 0 && (
                                <tr>
                                  <td colSpan="8" className="text-center">
                                    No data found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <div>
                            Showing {(currentPage - 1) * perPage + 1} to{" "}
                            {Math.min(indexOfLastItem, filteredData.length)} of{" "}
                            {filteredData.length} entries
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <MdOutlineKeyboardDoubleArrowLeft
                              onClick={() => {
                                if (currentPage > 1) setCurrentPage(1);
                              }}
                              style={{
                                cursor:
                                  currentPage > 1 ? "pointer" : "not-allowed",
                                opacity: currentPage > 1 ? 1 : 0.5,
                              }}
                            />

                            <MdOutlineKeyboardArrowLeft
                              onClick={() => {
                                if (currentPage > 1)
                                  setCurrentPage((prev) => prev - 1);
                              }}
                              style={{
                                cursor:
                                  currentPage > 1 ? "pointer" : "not-allowed",
                                opacity: currentPage > 1 ? 1 : 0.5,
                              }}
                            />

                            <span>
                              Page {currentPage} of {totalPages}
                            </span>

                            <MdOutlineKeyboardArrowRight
                              onClick={() => {
                                if (currentPage < totalPages)
                                  setCurrentPage((prev) => prev + 1);
                              }}
                              style={{
                                cursor:
                                  currentPage < totalPages
                                    ? "pointer"
                                    : "not-allowed",
                                opacity: currentPage < totalPages ? 1 : 0.5,
                              }}
                            />

                            <MdOutlineKeyboardDoubleArrowRight
                              onClick={() => {
                                if (currentPage < totalPages)
                                  setCurrentPage(totalPages);
                              }}
                              style={{
                                cursor:
                                  currentPage < totalPages
                                    ? "pointer"
                                    : "not-allowed",
                                opacity: currentPage < totalPages ? 1 : 0.5,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
      <AnimatePresence>
        {showEditPassword && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              zIndex: 1100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="shadow-lg"
              style={{
                background: "#fff",
                borderRadius: "16px",
                width: "95%",
                maxWidth: "500px",
                overflow: "hidden",
              }}
            >
              {/* Modal Header */}
              <div
                className="d-flex justify-content-between align-items-center p-3"
                style={{ borderBottom: "1px solid #eee" }}
              >
                <h5 className="mb-0">🔒 Set New Password</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closePasswordModal}
                ></button>
              </div>

              {/* Modal Body */}
              <div className="p-4">
                {/* Current Password */}
                <label className="fw-bold mb-1">Current Password</label>
                <div className="input-group mb-3">
                  <input
                    type="text"
                    autoComplete="current-password"
                    className="form-control form-control form-control-xs"
                    placeholder="Current password"
                    value={currentPasswordVisible}
                    disabled
                  />
                </div>

                <label className="fw-bold mb-1">New Password</label>
                <input
                  type="text"
                  autoComplete="new-password"
                  className="form-control form-control form-control-xs"
                  placeholder="Enter new password"
                  value={newPassword}
                  disabled
                />
                <button
                  type="button"
                  className="btn btn-link  fw-bold"
                  onClick={generatePassword}
                  style={{ textDecoration: "none" }}
                >
                  <i className="fa fa-magic me-1"></i> Generate Password
                </button>
                {/* <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 200 }}
                  overlay={renderTooltip}
                >
                  <div className="input-group mb-3">
                    <input
                      type={isNewPasswordVisible ? "text" : "password"}
                      autoComplete="new-password"
                      className="form-control form-control-xs"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <span
                      className="input-group-text"
                      onClick={() =>
                        setIsNewPasswordVisible(!isNewPasswordVisible)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {isNewPasswordVisible ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </OverlayTrigger> */}
              </div>

              {/* Modal Footer */}
              <div
                className="d-flex justify-content-end gap-2 p-3"
                style={{ borderTop: "1px solid #eee" }}
              >
                <button
                  className="btn btn-outline-secondary"
                  onClick={closePasswordModal}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSubmit}>
                  Save Password
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Schoollist;
